import { Box, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { FC, memo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { AppTheme } from '../../settings/appTheme';
import Constants from '../../store/constants';
import { EventInformationType } from '../../core/models/events';
import { SkeletonEventCarousel } from '../skeletons/skeletonEventCarousel';

const useStyles = makeStyles<void, 'gridTitleImage' | 'gradientImg'>()((_theme, _params, classes) => ({
  box: {
    overflowX: 'scroll',
    overflowY: 'hidden',
    marginTop: 20,
    height: '250px',
    '&::-webkit-scrollbar': {
      height: `${AppTheme.scrollbarHeight}`,
    },
  },
  gridList: {
    flexWrap: 'nowrap',
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: 'translateZ(0)',
    width: '100%',
    marginBottom: 20,
  },
  gridTitle: {
    height: '175px',
    cursor: 'pointer',
    margin: '0px 24px 12px 0px',
    width: '240px',
  },
  gridTitleImage: {
    height: '170px',
    width: '240px',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0px 8px 15px rgb(0 0 0 / 45%)',
  },
  gradientImg: {
    position: 'absolute',
    width: '240px',
    height: '70px',
    left: '0px',
    top: '100px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
    opacity: 0.7,
    borderRadius: '0px 0px 8px 8px',
  },
  subTitleText: {
    color: '#6B6B6B',
  },
  gridText: {
    marginTop: 10,
    marginBottom: 6,
  },
  linkStyle: {
    '&:hover': {
      color: 'rgb(178, 178, 178)',
      filter: 'brightness(60%)',
    },
  },
  gridImg: {
    position: 'relative',
    '&:hover': {
      [`& .${classes.gridTitleImage}, .${classes.gradientImg}`]: {
        filter: 'brightness(60%)',
      },
    },
  },
  titleText: {
    fontSize: 16,
    lineHeight: '16px',
  },
}));

const LOAD_MORE_THRESHOLD = 400;

export const EventCarousel: FC<EventCarouselType> = memo(({ events, isLoading, getItemLink, onLoadMore, isLoadingMore }) => {
  const { classes } = useStyles();
  const { handleEventLink } = useRoutesHelper();
  const boxRef = useRef<HTMLElement>(null);

  const handleScroll = useCallback(() => {
    if (!onLoadMore || isLoading || isLoadingMore) return;
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [onLoadMore, isLoading, isLoadingMore]);

  return (
    <Box ref={boxRef} className={`carousel ${classes.box}`} onScroll={handleScroll}>
      <Grid container className={classes.gridList}>
        {isLoading && <SkeletonEventCarousel skeletonCount={3} />}

        {events?.length > 0 &&
          !isLoading &&
          events.map((item: EventInformationType) => (
            <Grid item key={item.slug} className={classes.gridTitle}>
              <Link
                underline={'none'}
                onClick={() => handleEventLink(item.domainTypes, getItemLink(item.slug))}
                className={classes.linkStyle}
              >
                <Grid container direction="row">
                  <Grid item lg={12} xs={12} sm={12} md={12} xl={12} className={classes.gridImg}>
                    <img src={item.posterUrl} alt={item.title} className={classes.gridTitleImage} loading="lazy" />
                    <div className={classes.gradientImg} />
                  </Grid>
                  <Grid item lg={12} xs={12} sm={12} md={12} xl={12} className={classes.gridText}>
                    <Typography className={classes.titleText}>{item.title}</Typography>
                  </Grid>
                  <Grid item lg={12} xs={12} sm={12} md={12} xl={12} style={{ position: 'relative' }}>
                    <Typography variant="caption" className={classes.subTitleText}>
                      {`${dayjs(item.eventDate).format(Constants.DateFormat)}, ${item.city}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          ))}

        {isLoadingMore && <SkeletonEventCarousel skeletonCount={3} />}
      </Grid>
    </Box>
  );
});

type EventCarouselType = {
  events: EventInformationType[];
  getItemLink: (itemKey: string) => string;
  reloadDependencyValue?: any;
  isLoading: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
};
